import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaWhatsapp, 
  FaSlack,
  FaGoogle,
  FaMicrosoft,
  FaStripe,
  FaEnvelope,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaChartLine,
  FaPhoneAlt,
  FaSave, 
  FaMobileAlt, 
  FaMagic, 
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaSearch,
  FaFilter,
  FaTimes,
  FaCheck,
  FaCog,
  FaExternalLinkAlt,
  FaLock,
  FaQuestionCircle
} from "react-icons/fa";
import { SiZapier, SiGoogledrive, SiDropbox, SiZoom, SiQuickbooks, SiPaypal } from "react-icons/si";

const INTEGRATIONS = [
  // Communication
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    category: 'Communication',
    description: 'Send shift notifications and updates via WhatsApp',
    icon: FaWhatsapp,
    color: 'bg-green-500',
    iconColor: 'text-green-500',
    borderColor: 'border-green-200',
    bgColor: 'bg-green-50',
    popular: true,
    features: ['Automated notifications', 'Shift reminders', 'Two-way messaging', 'Template support'],
    setupTime: '5 min',
    docsUrl: 'https://developers.facebook.com/docs/whatsapp/cloud-api',
    apiKeyRequired: true,
    setupSteps: [
      'Create a WhatsApp Business Account at business.facebook.com',
      'Set up WhatsApp Business API in Meta Business Suite',
      'Get your Phone Number ID and Access Token',
      'Add webhook URL for receiving messages',
      'Configure message templates in Meta dashboard'
    ],
    envVars: ['WHATSAPP_PHONE_NUMBER_ID', 'WHATSAPP_ACCESS_TOKEN', 'WHATSAPP_WEBHOOK_VERIFY_TOKEN']
  },
  
  // Calendar
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    category: 'Calendar',
    description: 'Sync shifts to Google Calendar automatically',
    icon: FaGoogle,
    color: 'bg-yellow-500',
    iconColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    bgColor: 'bg-yellow-50',
    popular: true,
    features: ['Auto-sync shifts', 'Event reminders', 'Color coding', 'Shared calendars'],
    setupTime: '2 min',
    docsUrl: 'https://developers.google.com/calendar/api/guides/overview',
    apiKeyRequired: true,
    setupSteps: [
      'Go to Google Cloud Console (console.cloud.google.com)',
      'Create new project and enable Google Calendar API',
      'Create OAuth 2.0 credentials',
      'Add authorized redirect URIs',
      'Download credentials JSON file'
    ],
    envVars: ['GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_SECRET', 'GOOGLE_REDIRECT_URI']
  }
];

export default function IntegrationPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [connectedIntegrations, setConnectedIntegrations] = useState(new Set(['whatsapp', 'google-calendar']));
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  
  // WhatsApp specific state
  const [whatsappConfig, setWhatsappConfig] = useState({
    enabled: true,
    phoneNumber: "",
    messageTemplate: "Hello {{name}}, your shift for {{date}} at {{location}} has been confirmed."
  });

  const categories = ["All", "Communication", "Calendar"];

  const filteredIntegrations = INTEGRATIONS.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const connectedCount = connectedIntegrations.size;
  const popularIntegrations = INTEGRATIONS.filter(i => i.popular);

  const handleConnect = (integrationId) => {
    const newConnected = new Set(connectedIntegrations);
    if (newConnected.has(integrationId)) {
      newConnected.delete(integrationId);
    } else {
      newConnected.add(integrationId);
    }
    setConnectedIntegrations(newConnected);
  };

  const handleConfigure = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 backdrop-blur-lg bg-white/90">
        <div className="px-6 lg:px-12 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-3 rounded-xl shadow-lg">
                  <FaCog className="text-2xl text-white" />
                </div>
                Integration Hub
              </h1>
              <p className="mt-2 text-gray-600">
                Connect Shiftry with your favorite tools and automate your workflow
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-6 py-3">
                <div className="text-sm text-gray-600">Connected</div>
                <div className="text-2xl font-bold text-blue-600">{connectedCount}</div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-6 py-3">
                <div className="text-sm text-gray-600">Available</div>
                <div className="text-2xl font-bold text-green-600">{INTEGRATIONS.length}</div>
              </div>
            </div>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="px-6 lg:px-12 py-8">
        
        {/* POPULAR INTEGRATIONS */}
        {selectedCategory === "All" && searchQuery === "" && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <FaChartLine className="text-orange-500 text-xl" />
              <h2 className="text-2xl font-bold text-gray-900">Popular Integrations</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  isConnected={connectedIntegrations.has(integration.id)}
                  onConnect={handleConnect}
                  onConfigure={handleConfigure}
                />
              ))}
            </div>
          </div>
        )}

        {/* ALL INTEGRATIONS */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {selectedCategory === "All" ? "All Integrations" : selectedCategory}
          </h2>
          
          {filteredIntegrations.length === 0 ? (
            <div className="text-center py-20">
              <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No integrations found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  isConnected={connectedIntegrations.has(integration.id)}
                  onConnect={handleConnect}
                  onConfigure={handleConfigure}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CONFIGURATION MODAL */}
      <AnimatePresence>
        {showConfigModal && selectedIntegration && (
          <ConfigModal
            integration={selectedIntegration}
            isConnected={connectedIntegrations.has(selectedIntegration.id)}
            onClose={() => setShowConfigModal(false)}
            onConnect={handleConnect}
            whatsappConfig={whatsappConfig}
            setWhatsappConfig={setWhatsappConfig}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// INTEGRATION CARD COMPONENT
function IntegrationCard({ integration, isConnected, onConnect, onConfigure }) {
  const Icon = integration.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className={`bg-white rounded-2xl border-2 ${
        isConnected ? integration.borderColor : 'border-gray-200'
      } p-6 transition-all hover:shadow-xl relative overflow-hidden group`}
    >
      {/* Connected Badge */}
      {isConnected && (
        <div className="absolute top-4 right-4">
          <div className={`${integration.bgColor} ${integration.iconColor} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
            <FaCheck className="text-xs" />
            Connected
          </div>
        </div>
      )}

      {/* Icon */}
      <div className={`${integration.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
        <Icon className="text-2xl text-white" />
      </div>

      {/* Content */}
      <h3 className="text-xl font-bold text-gray-900 mb-2">{integration.name}</h3>
      <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{integration.description}</p>

      {/* Features */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1.5">
          {integration.features.slice(0, 3).map((feature, index) => (
            <span
              key={index}
              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md"
            >
              {feature}
            </span>
          ))}
          {integration.features.length > 3 && (
            <span className="text-xs text-gray-500 px-2 py-1">
              +{integration.features.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Setup Time */}
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
        <FaClock />
        <span>{integration.setupTime} setup</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onConnect(integration.id)}
          className={`flex-1 py-2.5 px-4 rounded-xl font-semibold transition-all ${
            isConnected
              ? 'bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100'
              : `${integration.color} text-white hover:opacity-90 shadow-lg`
          }`}
        >
          {isConnected ? 'Disconnect' : 'Connect'}
        </button>
        {isConnected && (
          <button
            onClick={() => onConfigure(integration)}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
          >
            <FaCog className="text-gray-700" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

// CONFIGURATION MODAL
function ConfigModal({ integration, isConnected, onClose, onConnect, whatsappConfig, setWhatsappConfig }) {
  const Icon = integration.icon;
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      onClose();
    }, 1500);
  };

  const insertVariable = (variable) => {
    setWhatsappConfig(prev => ({
      ...prev,
      messageTemplate: prev.messageTemplate + ` {{${variable}}}`
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className={`${integration.color} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <Icon className="text-3xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{integration.name}</h2>
                <p className="text-white/90 text-sm">{integration.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-all"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {integration.id === 'whatsapp' ? (
            <WhatsAppConfig
              config={whatsappConfig}
              setConfig={setWhatsappConfig}
              insertVariable={insertVariable}
            />
          ) : (
            <GenericConfig integration={integration} />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex gap-3">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`flex-1 ${integration.color} text-white py-3 rounded-xl font-semibold 
            hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FaSave />
                Save Configuration
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-all"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// WHATSAPP SPECIFIC CONFIG
function WhatsAppConfig({ config, setConfig, insertVariable }) {
  return (
    <div className="space-y-6">
      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div>
          <h3 className="font-semibold text-gray-900">Enable Notifications</h3>
          <p className="text-sm text-gray-600">Turn on automatic WhatsApp messaging</p>
        </div>
        <button
          onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
          className={`relative w-14 h-8 rounded-full transition-colors ${
            config.enabled ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full shadow transform transition-transform ${
              config.enabled ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Phone Number */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Test Phone Number
        </label>
        <div className="relative">
          <FaPhoneAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={config.phoneNumber}
            onChange={(e) => setConfig(prev => ({ ...prev, phoneNumber: e.target.value }))}
            placeholder="+1 (555) 000-0000"
            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
            focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Message Template */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Message Template
        </label>
        <textarea
          value={config.messageTemplate}
          onChange={(e) => setConfig(prev => ({ ...prev, messageTemplate: e.target.value }))}
          rows={5}
          className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl 
          focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
        />
        
        {/* Variable Buttons */}
        <div className="flex flex-wrap gap-2 mt-3">
          {['name', 'date', 'shift_time', 'location', 'duration'].map((variable) => (
            <button
              key={variable}
              onClick={() => insertVariable(variable)}
              className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 
              px-3 py-1.5 rounded-lg transition-all font-medium"
            >
              {`{{${variable}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaWhatsapp className="text-green-500" />
          Message Preview
        </h4>
        <div className="bg-white rounded-lg p-3 text-sm text-gray-700 shadow-sm">
          {config.messageTemplate || "Enter a message template..."}
        </div>
      </div>
    </div>
  );
}

// GENERIC CONFIG
function GenericConfig({ integration }) {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  return (
    <div className="space-y-6">
      {/* Setup Instructions */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FaCog className="text-blue-600" />
          Step-by-Step Setup Guide
        </h3>
        <ol className="space-y-3">
          {integration.setupSteps?.map((step, index) => (
            <li key={index} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                {index + 1}
              </span>
              <span className="text-sm text-gray-700 flex-1">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* API Key Configuration */}
      {integration.apiKeyRequired && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">API Configuration</h3>
          
          {integration.envVars?.map((envVar, index) => (
            <div key={index} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {envVar}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={`Enter your ${envVar.toLowerCase().replace(/_/g, ' ')}`}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <FaLock /> : <FaCheckCircle />}
                </button>
              </div>
            </div>
          ))}
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
            <p className="text-xs text-yellow-800 flex items-center gap-2">
              <FaLock className="flex-shrink-0" />
              These values should be stored in your .env file and never committed to version control
            </p>
          </div>
        </div>
      )}

      {/* Features */}
      <div className={`${integration.bgColor} border ${integration.borderColor} rounded-xl p-6`}>
        <h3 className="font-semibold text-gray-900 mb-4">Features & Capabilities</h3>
        <ul className="space-y-2">
          {integration.features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-gray-700">
              <FaCheck className={integration.iconColor} />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* Documentation Link */}
      {integration.docsUrl && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <FaExternalLinkAlt className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Official Documentation</h4>
                <p className="text-xs text-gray-600">Complete API reference and guides</p>
              </div>
            </div>
            <a
              href={integration.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium"
            >
              View Docs
            </a>
          </div>
        </div>
      )}

      {/* Backend Implementation Guide */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FaCheckCircle className="text-purple-600" />
          Backend Implementation
        </h3>
        <div className="space-y-3 text-sm text-gray-700">
          <p className="font-medium">To implement this integration in your backend:</p>
          
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-mono text-xs text-gray-800 mb-2">1. Install required packages:</p>
            <code className="block bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
              {integration.id === 'whatsapp' && 'npm install whatsapp-cloud-api'}
              {integration.id === 'slack' && 'npm install @slack/web-api @slack/bolt'}
              {integration.id === 'teams' && 'npm install @microsoft/microsoft-graph-client'}
              {integration.id === 'email' && 'npm install @sendgrid/mail'}
              {integration.id === 'sms' && 'npm install twilio'}
              {integration.id === 'google-calendar' && 'npm install googleapis'}
              {integration.id === 'stripe' && 'npm install stripe'}
              {integration.id === 'quickbooks' && 'npm install node-quickbooks'}
              {!['whatsapp', 'slack', 'teams', 'email', 'sms', 'google-calendar', 'stripe', 'quickbooks'].includes(integration.id) && 
                `npm install ${integration.id}-sdk`}
            </code>
          </div>

          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-mono text-xs text-gray-800 mb-2">2. Create integration route:</p>
            <code className="block bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
              backend/src/routes/{integration.id}Routes.js
            </code>
          </div>

          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <p className="font-mono text-xs text-gray-800 mb-2">3. Add environment variables to .env:</p>
            <code className="block bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto whitespace-pre">
              {integration.envVars?.join('\n')}
            </code>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex gap-3">
          <FaLock className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Security Best Practices</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Store API keys in environment variables, never in code</li>
              <li>• Use OAuth 2.0 for user authentication when available</li>
              <li>• Implement rate limiting and request validation</li>
              <li>• Regularly rotate API keys and secrets</li>
              <li>• Monitor API usage for suspicious activity</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}